import { MEDIA_TYPE_3D_MODEL } from './model-3d.js';
import {
	PRODUCT_MEDIA_3D_VIEWER_LOAD_FAILED_ERROR_REASON,
	trackProductMedia3dModelInteracted,
	trackProductMedia3dViewerLoadFailed,
	trackProductMedia3dViewerLoaded,
} from './product-media-3d-launch-analytics.js';
import { PRODUCT_MEDIA_3D_VIEWER_OUTCOME } from './product-media-3d-metric.js';
import {
	PRODUCT_MEDIA_3D_VIEWER_RENDER_ERROR_REASON,
	emitProductMedia3dViewerObservability,
} from './product-media-3d-observability.js';
import {
	dispatchProductMedia3dModelInteracted,
	dispatchProductMedia3dViewerLoadFailed,
	dispatchProductMedia3dViewerLoaded,
} from './product-media-3d-viewer.js';

/** Matches legacy product_images hero video area (`product_images.scss`). */
export const HERO_3D_MODEL_MIN_HEIGHT_PX = 260;

/**
 * @returns {number}
 */
function nowMs() {
	return typeof globalThis.performance?.now === 'function' ? globalThis.performance.now() : Date.now();
}

/**
 * @param {string} modelUrl
 * @param {{
 *   catalog?: string,
 *   skuStripped?: string,
 * }} [trackingContext]
 * @returns {{
 *   catalog?: string,
 *   mediaType: string,
 *   modelUrl: string,
 *   skuStripped?: string,
 * }}
 */
export function buildProductMedia3dMetricDetail(modelUrl, trackingContext = {}) {
	/** @type {{
	 *   catalog?: string,
	 *   mediaType: string,
	 *   modelUrl: string,
	 *   skuStripped?: string,
	 * }} */
	const metricDetail = {
		modelUrl,
		mediaType: MEDIA_TYPE_3D_MODEL,
	};

	if (typeof trackingContext.catalog === 'string' && trackingContext.catalog !== '') {
		metricDetail.catalog = trackingContext.catalog;
	}

	if (typeof trackingContext.skuStripped === 'string' && trackingContext.skuStripped !== '') {
		metricDetail.skuStripped = trackingContext.skuStripped;
	}

	return metricDetail;
}

/**
 * @param {{
 *   catalog?: string,
 *   mediaType: string,
 *   modelUrl: string,
 *   skuStripped?: string,
 * }} metricDetail
 */
function buildProductMedia3dTrackingDetail(metricDetail) {
	return {
		catalog: metricDetail.catalog,
		mediaType: metricDetail.mediaType,
		skuStripped: metricDetail.skuStripped,
	};
}

/**
 * @param {{
 *   catalog?: string,
 *   mediaType: string,
 *   modelUrl: string,
 *   skuStripped?: string,
 * }} metricDetail
 * @param {typeof emitProductMedia3dViewerObservability} [emitViewerOutcome] Attach-scoped emit,
 *   carrying the load duration and the once-per-session guard.
 */
export function handleProductMedia3dViewerLoaded(metricDetail, emitViewerOutcome = emitProductMedia3dViewerObservability) {
	dispatchProductMedia3dViewerLoaded(metricDetail);
	trackProductMedia3dViewerLoaded(buildProductMedia3dTrackingDetail(metricDetail));
	emitViewerOutcome({
		metricDetail,
		outcome: PRODUCT_MEDIA_3D_VIEWER_OUTCOME.loaded,
	});
}

/**
 * @param {{
 *   catalog?: string,
 *   mediaType: string,
 *   modelUrl: string,
 *   skuStripped?: string,
 * }} metricDetail
 * @param {string} [errorReason]
 * @param {typeof emitProductMedia3dViewerObservability} [emitViewerOutcome] Attach-scoped emit,
 *   carrying the load duration and the once-per-session guard.
 */
export function handleProductMedia3dViewerLoadFailed(
	metricDetail,
	errorReason = PRODUCT_MEDIA_3D_VIEWER_LOAD_FAILED_ERROR_REASON,
	emitViewerOutcome = emitProductMedia3dViewerObservability,
) {
	dispatchProductMedia3dViewerLoadFailed(metricDetail);
	trackProductMedia3dViewerLoadFailed({
		...buildProductMedia3dTrackingDetail(metricDetail),
		errorReason,
	});
	emitViewerOutcome({
		errorReason,
		metricDetail,
		outcome: PRODUCT_MEDIA_3D_VIEWER_OUTCOME.loadFailed,
	});
}

/** `<model-viewer>` reports a lost WebGL context under this error detail type. */
const MODEL_VIEWER_WEBGL_CONTEXT_LOST_ERROR_TYPE = 'webglcontextlost';

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isProductMedia3dViewerRenderError(event) {
	return event?.detail?.type === MODEL_VIEWER_WEBGL_CONTEXT_LOST_ERROR_TYPE;
}

/**
 * Observability only. The load already succeeded, so GA4 load events and the shopper-facing error
 * state both stay untouched.
 *
 * @param {{
 *   catalog?: string,
 *   mediaType: string,
 *   modelUrl: string,
 *   skuStripped?: string,
 * }} metricDetail
 * @param {typeof emitProductMedia3dViewerObservability} [emitRenderOutcome]
 */
export function handleProductMedia3dViewerRenderFailed(metricDetail, emitRenderOutcome = emitProductMedia3dViewerObservability) {
	emitRenderOutcome({
		errorReason: PRODUCT_MEDIA_3D_VIEWER_RENDER_ERROR_REASON,
		metricDetail,
		outcome: PRODUCT_MEDIA_3D_VIEWER_OUTCOME.renderFailed,
	});
}

/**
 * @param {{
 *   catalog?: string,
 *   mediaType: string,
 *   modelUrl: string,
 *   skuStripped?: string,
 * }} metricDetail
 * @param {Event} event
 */
export function handleProductMedia3dModelInteracted(metricDetail, event) {
	if (event?.detail?.source === 'user-interaction') {
		dispatchProductMedia3dModelInteracted(metricDetail);
		trackProductMedia3dModelInteracted(buildProductMedia3dTrackingDetail(metricDetail));
	}
}

/**
 * @param {HTMLElement} modelViewerElement
 * @param {{
 *   catalog?: string,
 *   mediaType: string,
 *   modelUrl: string,
 *   skuStripped?: string,
 * }} metricDetail
 * @param {{ onViewerLoadFailed?: () => void }} [options]
 * @returns {() => void}
 */
export function attachProductMedia3dViewerListeners(modelViewerElement, metricDetail, options = {}) {
	const attachStartedAt = nowMs();
	let emittedLoadOutcome = '';
	let hasEmittedRenderOutcome = false;
	let hasTrackedModelInteraction = false;

	// Repeated model-viewer error events describe one failed load. Emitting each one would skew the
	// failure rate the Datadog monitor computes against the loaded count.
	const emitLoadOutcome = (viewerOutcome) => {
		if (emittedLoadOutcome !== '') {
			return;
		}

		emittedLoadOutcome = viewerOutcome.outcome;
		emitProductMedia3dViewerObservability({
			...viewerOutcome,
			loadDurationMs: nowMs() - attachStartedAt,
		});
	};

	// A render failure follows a load that already reported its own outcome. Guarding it separately
	// keeps a lost WebGL context visible instead of being swallowed by the load emit.
	// The context loss happens long after the load, so no load_duration_ms is reported.
	const emitRenderOutcome = (viewerOutcome) => {
		if (hasEmittedRenderOutcome) {
			return;
		}

		hasEmittedRenderOutcome = true;
		emitProductMedia3dViewerObservability(viewerOutcome);
	};

	const onViewerLoaded = () => handleProductMedia3dViewerLoaded(metricDetail, emitLoadOutcome);
	const onViewerLoadFailed = (event) => {
		if (isProductMedia3dViewerRenderError(event)) {
			// A lost context is a render failure only for a session already counted as loaded. Lose it
			// before the load resolves and the shopper never saw a model: that is the load failure below.
			if (emittedLoadOutcome === PRODUCT_MEDIA_3D_VIEWER_OUTCOME.loaded) {
				handleProductMedia3dViewerRenderFailed(metricDetail, emitRenderOutcome);

				return;
			}

			if (emittedLoadOutcome !== '') {
				return;
			}
		}

		// A fixed reason is all the event supports. `<model-viewer>` raises exactly two error detail
		// types, `webglcontextlost` and `loadfailure`, and a failed fetch, a 403 and an unparseable
		// GLB all arrive as `loadfailure`. Its `sourceError` carries only a free-text message.
		// Network failures therefore collapse into the generic load failure here. A caller that
		// establishes the cause some other way passes its own reason to the handler.
		handleProductMedia3dViewerLoadFailed(metricDetail, PRODUCT_MEDIA_3D_VIEWER_LOAD_FAILED_ERROR_REASON, emitLoadOutcome);
		options.onViewerLoadFailed?.();
	};
	const onModelInteracted = (event) => {
		if (event?.detail?.source !== 'user-interaction' || hasTrackedModelInteraction) {
			return;
		}

		hasTrackedModelInteraction = true;
		handleProductMedia3dModelInteracted(metricDetail, event);
	};

	modelViewerElement.addEventListener('load', onViewerLoaded);
	modelViewerElement.addEventListener('error', onViewerLoadFailed);
	modelViewerElement.addEventListener('camera-change', onModelInteracted);

	if (modelViewerElement.loaded === true) {
		handleProductMedia3dViewerLoaded(metricDetail, emitLoadOutcome);
	}

	return () => {
		modelViewerElement.removeEventListener('load', onViewerLoaded);
		modelViewerElement.removeEventListener('error', onViewerLoadFailed);
		modelViewerElement.removeEventListener('camera-change', onModelInteracted);
	};
}

/**
 * @param {HTMLElement} containerElement
 * @param {{
 *   catalog?: string,
 *   mediaType: string,
 *   modelUrl: string,
 *   skuStripped?: string,
 * }} metricDetail
 * @param {() => boolean} isCancelled
 * @param {{ onViewerLoadFailed?: () => void }} [options]
 * @returns {() => void}
 */
export function watchAndAttachProductMedia3dViewerListeners(containerElement, metricDetail, isCancelled, options = {}) {
	let removeListeners = () => {};
	/** @type {MutationObserver | null} */
	let observer = null;

	function tryAttach() {
		if (isCancelled()) {
			return true;
		}

		const modelViewerElement = containerElement.querySelector('model-viewer');
		if (modelViewerElement) {
			removeListeners = attachProductMedia3dViewerListeners(modelViewerElement, metricDetail, options);
			return true;
		}

		return false;
	}

	if (tryAttach()) {
		return () => {
			removeListeners();
		};
	}

	if (typeof MutationObserver === 'function') {
		observer = new MutationObserver(() => {
			if (tryAttach() && observer !== null) {
				observer.disconnect();
				observer = null;
			}
		});
		observer.observe(containerElement, { childList: true, subtree: true });
	}

	return () => {
		if (observer !== null) {
			observer.disconnect();
		}
		removeListeners();
	};
}
